// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { BundleApplyLine } from '@/bundle/components/apply-bundle-dialog';
import {
  BundleAutoSumResult,
  ExistingBundleRow,
  computeBundleAutoSum,
} from '@/bundle/utilities/bundle-auto-sum';

interface PhysicalBundleApplyResult extends BundleAutoSumResult {
  /** Members that resulted in a line change (summed into a row or appended). */
  appliedCount: number;
  /**
   * Members not applied because they are non-physical (services cannot go on
   * these surfaces) or because appending them would exceed the row cap.
   */
  skippedCount: number;
}

/**
 * Computes a bundle apply for a physical-only surface (FO / PO / RO /
 * shipment). These surfaces hold only physical line items and cap the row
 * count, so non-physical (service) members are dropped — matching the
 * physical-only item picker — and appends are limited so the total never
 * exceeds maxRows. Auto-sum updates into existing rows and fills of a trailing
 * blank row are always applied because they do not grow the row count.
 */
export function computePhysicalBundleApply(
  existingRows: ExistingBundleRow[],
  lines: BundleApplyLine[],
  maxRows: number
): PhysicalBundleApplyResult {
  const physicalLines = lines.filter((line) => line.item.type !== 'SERVICE');
  const { updates, appends, fills, combined } = computeBundleAutoSum(
    existingRows,
    physicalLines
  );
  const room = Math.max(0, maxRows - existingRows.length);
  const cappedAppends = appends.slice(0, room);
  const appliedCount = updates.length + fills.length + cappedAppends.length;
  return {
    updates,
    appends: cappedAppends,
    fills,
    combined,
    appliedCount,
    skippedCount: lines.length - appliedCount,
  };
}
